"use client";
import Button from "../components/ui/button";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import Nigeria from "../assets/images/countries/Nigeria.svg";
import upload from "../assets/images/document-upload.svg";
import axios from "axios";

import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Swal from "sweetalert2";
import { RadioGroup, RadioGroupItem } from "../../v2/components/ui/radio-group";

export const handleUploadFileUtils = async ({ selectedFile, name }) => {
  try {
    const formData = new FormData();
    formData.append("file", selectedFile);

    const token = localStorage.getItem("token");

    const response = await axios.post("/v1/utility/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    const url = response?.data;
    return url;
  } catch (error) {
    return (
      error?.response?.data || {
        message: "An error occurred while uploading the file",
      }
    );
  }
};

const ApproveRequest = () => {
  const [offeredAmount, setOfferedAmount] = useState("");
  const [paymentSource, setPaymentSource] = useState("");
  const [interestRate, setInterestRate] = useState(6);
  const [tenure, setTenure] = useState("90 Days");
  const [repaymentStructure, setRepaymentStructure] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [debitManagementFee, setDebitManagementFee] = useState(false);

  const [loanDetails, setLoanDetails] = useState(null);
  console.log(loanDetails, "loanDetails");
  const requestedAmount = loanDetails?.data?.requestedAmount;

  const [errors, setErrors] = useState({
    offeredAmount: "",
    interestRate: "",
    repaymentStructure: "",
    uploadedFile: "",
  });

  const navigate = useNavigate();
  const { id } = useParams();

  const totalRepay = useMemo(() => {
    const amount = Number(offeredAmount) || 0;
    const rate = Number(interestRate) || 0;
    return amount + (amount * rate) / 100;
  }, [offeredAmount, interestRate]);

  const managementFee = 1;
  const currency = loanDetails?.data?.currency || "NGN";

  const tenureOptions = ["30 Days", "60 Days", "90 Days", "120 Days", "180 Days"];

  const repaymentOptions = ["EqualRepayment", "BulletPayment"];
  const hasError = Object.values(errors).some((error) => error !== "");

  useEffect(() => {
    const fetchLoanDetails = async () => {
      if (!id) return;
      try {
        const token = localStorage.getItem("token");

        const response = await axios(`/v1/admin/finance/single/${id}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response?.data?.error) {
          const data = await response?.data;
          setLoanDetails(data);

          if (data?.data?.requestedAmount) {
            setOfferedAmount(data?.data?.requestedAmount);
          }
        } else {
          console.error("Failed to fetch loan details");
        }
      } catch (error) {
        console.error("Error fetching loan details:", error);
      }
    };

    fetchLoanDetails();
  }, [id]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileTypes = ["JPEG", "JPG", "PNG", "PDF"];
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          uploadedFile: "Please upload a JPEG, JPG, PNG, or PDF file",
        }));
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          uploadedFile: "File size must be less than 10MB",
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        uploadedFile: "",
      }));

      setIsUploading(true);

      try {
        const uploadResponse = await handleUploadFileUtils({
          selectedFile: file,
          name: file.name,
        });

        if (uploadResponse?.link || uploadResponse?.url) {
          setUploadedFile(file);
          setUploadedFileUrl(uploadResponse?.link || uploadResponse?.url);
          console.log("File uploaded successfully:", uploadResponse);
        } else if (uploadResponse?.message) {
          setErrors((prev) => ({
            ...prev,
            uploadedFile: uploadResponse.message,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            uploadedFile: "Failed to upload file. Please try again.",
          }));
        }
      } catch (error) {
        console.error("Upload error:", error);
        setErrors((prev) => ({
          ...prev,
          uploadedFile: "Failed to upload file. Please try again.",
        }));
      } finally {
        setIsUploading(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {
      offeredAmount: "",
      interestRate: "",
      repaymentStructure: "",
      uploadedFile: "",
    };

    const amountNum = Number(offeredAmount);
    if (!offeredAmount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.offeredAmount = "Offered amount must be a valid number greater than 0";
    }

    const rateNum = Number(interestRate);
    if (!interestRate || isNaN(rateNum) || rateNum <= 0) {
      newErrors.interestRate = "Interest rate must be a valid number greater than 0";
    } else if (rateNum > 100) {
      newErrors.interestRate = "Interest rate cannot exceed 100%";
    }

    if (!repaymentStructure) {
      newErrors.repaymentStructure = "Please select a repayment structure";
    }

    if (!uploadedFileUrl) {
      newErrors.uploadedFile = "Please upload a signed loan service agreement";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const getTenureInDays = (tenureString) => {
    const days = Number.parseInt(tenureString.split(" ")[0]);
    return days;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        text: "Authentication token not found. Please log in again.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const tenureInDays = getTenureInDays(tenure);
      const amountValue = Number(offeredAmount);
      const interestRateValue = Number(interestRate) / 100;
      const managementFeeValue = Number(managementFee);

      if (
        isNaN(amountValue) ||
        isNaN(interestRateValue) ||
        isNaN(tenureInDays) ||
        isNaN(managementFeeValue)
      ) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          text: "Invalid numeric values detected. Please check your inputs.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        setIsLoading(false);
        return;
      }

      const formData = new URLSearchParams();
      formData.append("repaymentStructure", repaymentStructure || "");
      formData.append("amount", amountValue);
      formData.append("currency", currency || "NGN");
      formData.append("tenure", tenureInDays);
      formData.append("interestRate", interestRateValue);
      formData.append("managementFee", managementFeeValue);
      formData.append("managementFeeDebitWalletType", paymentSource);

      if (uploadedFileUrl) {
        formData.append("signedAgreement", uploadedFileUrl);
      }

      const response = await axios.post(`/v1/admin/new-finance/approve/${id}`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.data?.error) {
        const result = await response?.data;

        await Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Request approved successfully!",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          didClose: () => {
            window.location.href = `/finance/${id}`;
          },
        });
      } else {
        const error = await response?.data;
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          text: error.message || "An error occurred while approving the request",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        console.error("Error:", error);
      }
    } catch (error) {
      console.error("Network error:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        text: "Network error. Please try again.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-start w-1/2 gap-10 px-6 py-4 border-b border-gray-200">
        <button className="p-1 hover:bg-gray-100 rounded-[8px]">
          <span
            className="material-icons"
            style={{ color: "#344054", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            close
          </span>
        </button>
        <h2 className="text-base font-medium text-gray-900">Approve Request</h2>
      </div>

      <div className="flex justify-center ">
        <div className="w-full max-w-[592px]">
          <Card className="border-none">
            <CardHeader className="pb-6">
              <CardTitle className="text-xl font-semibold text-gray-900">
                Approve loan request
              </CardTitle>
              <p className="text-sm text-[#667085] mt-1">Update details of the agreed upon terms</p>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-6">
                {/* Offered Amount */}
                <div className="space-y-2">
                  <div className="text-sm font-bold text-gray-700">Offered amount</div>
                  <div className="flex rounded-[8px] gap-3">
                    <div className="flex items-center px-3 bg-[#ECEFF3] rounded-l-[8px]">
                      <div className="mr-2">
                        <img src={Nigeria || "/placeholder.svg"} alt="" />
                      </div>
                      <span className="text-sm font-medium text-[#98A2B3]">{currency}</span>
                    </div>
                    <Input
                      type="number"
                      value={offeredAmount}
                      onChange={(e) => {
                        console.log({ requestedAmount });
                        if (e.target.value > requestedAmount) {
                          setErrors((prev) => ({
                            ...prev,
                            offeredAmount: "Offered amount cannot exceed requested amount",
                          }));
                        } else {
                          setOfferedAmount(e.target.value);
                          if (errors.offeredAmount) {
                            setErrors((prev) => ({
                              ...prev,
                              offeredAmount: "",
                            }));
                          }
                        }
                      }}
                      max={offeredAmount}
                      className={`bg-[#ECEFF3] border-none flex-1 rounded-r-[8px] ${
                        errors.offeredAmount ? "ring-2 ring-red-500 bg-red-50" : ""
                      }`}
                      placeholder="Enter amount"
                      min="0"
                    />
                  </div>
                  {errors.offeredAmount && (
                    <p className="text-sm font-medium text-red-500">{errors.offeredAmount}</p>
                  )}
                  <p className="text-sm text-[#667085]">
                    1% management fee will be deducted at disbursal
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                  <div className="space-y-2">
                    <div htmlFor="interest-rate" className="text-sm font-bold">
                      Interest rate
                    </div>
                    <div className="relative">
                      <Input
                        id="interest-rate"
                        type="number"
                        value={interestRate}
                        onChange={(e) => {
                          setInterestRate(e.target.value);
                          if (errors.interestRate) {
                            setErrors((prev) => ({
                              ...prev,
                              interestRate: "",
                            }));
                          }
                        }}
                        className={`bg-[#ECEFF3] border-none py-6 pr-8 rounded-[8px] ${
                          errors.interestRate ? "ring-2 ring-red-500 bg-red-50" : ""
                        }`}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                    {errors.interestRate && (
                      <p className="text-sm font-medium text-red-500">{errors.interestRate}</p>
                    )}
                  </div>

                  <div className="ml-3 space-y-2">
                    <div className="text-sm font-bold">Tenure</div>
                    <Select value={tenure} onValueChange={setTenure} className="border-none">
                      <SelectTrigger className="border-none bg-[#ECEFF3] rounded-[8px]">
                        <SelectValue placeholder="Select tenure" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenureOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-[#667085]">
                    <div className="text-sm">
                      <span className="">Total to repay: </span>
                      <span className="font-semibold ">₦{totalRepay.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-bold">Repayment structure</div>
                  <Select
                    value={repaymentStructure}
                    onValueChange={(value) => {
                      setRepaymentStructure(value);
                      if (errors.repaymentStructure) {
                        setErrors((prev) => ({
                          ...prev,
                          repaymentStructure: "",
                        }));
                      }
                    }}
                  >
                    <SelectTrigger
                      className={`h-14 border-none bg-[#ECEFF3] rounded-[8px] ${
                        errors.repaymentStructure ? "ring-2 ring-red-500 bg-red-50" : ""
                      }`}
                    >
                      <SelectValue placeholder="Choose repayment structure" />
                    </SelectTrigger>
                    <SelectContent>
                      {repaymentOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.repaymentStructure && (
                    <p className="text-sm font-medium text-red-500">{errors.repaymentStructure}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-bold">Signed loan service agreement</div>
                  <div className="relative bg-[#ECEFF3] rounded-[8px]">
                    <input
                      type="file"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={handleFileUpload}
                      className="absolute inset-0 w-full h-[56px] opacity-0 cursor-pointer z-10"
                      disabled={isUploading}
                    />
                    <div
                      className={`bg-[#ECEFF3] rounded-[8px] p-6 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer bg-muted/30 ${
                        errors.uploadedFile ? "ring-2 ring-red-500 bg-red-50" : ""
                      } ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-center justify-start gap-2">
                        <img src={upload || "/placeholder.svg"} alt="" />
                        <span className="text-sm text-muted-foreground">
                          {isUploading
                            ? "Uploading..."
                            : uploadedFile
                            ? uploadedFile.name
                            : "Upload file (JPEG, JPG, PNG, PDF)"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {errors.uploadedFile && (
                    <p className="text-sm font-medium text-red-500">{errors.uploadedFile}</p>
                  )}
                  {uploadedFile && uploadedFileUrl && !errors.uploadedFile && (
                    <p className="mt-2 text-xs font-medium text-green-600">
                      ✓ File uploaded successfully
                    </p>
                  )}
                </div>

                {/* Management fee */}
                <div className="space-y-3">
                  <h3 className="text-[15px] font-semibold text-gray-900">Management fee</h3>
                  <RadioGroup
                    value={paymentSource}
                    onValueChange={(val) => setPaymentSource(val)}
                    className="flex items-center gap-6"
                  >
                    <div className="flex items-center space-x-2.5">
                      <RadioGroupItem value="TRADEVU" id="payment-usdc" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                      <p htmlFor="payment-usdc" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                        Deduct from {currency} wallet
                      </p>
                    </div>
                    <div className="flex items-center space-x-2.5">
                      <RadioGroupItem value="FINANCE" id="payment-loan" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                      <p className="text-[15px] font-medium cursor-pointer text-[#344054]">
                        Deduct from loan amount
                      </p>
                    </div>
                  </RadioGroup>

                    <p className="text-[15px] italic text-[#667085] mt-2">
                      Preferred source: <span className="font-semibold text-gray-900">Deduct from {currency} wallet</span>
                  </p>
                </div>

                <div className="flex justify-start pt-2">
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading || isUploading || hasError}
                    className="w-full rounded-[8px] md:w-auto px-8 py-3 bg-[#6F00FF] hover:bg-purple-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    size="lg"
                  >
                    {isLoading ? "Approving..." : "Approve request"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApproveRequest;
